<template>
  <div>
    <div class="page-header">
      <div>
        <div class="page-title">API 文档</div>
        <div class="page-subtitle">签名规则与请求约定</div>
      </div>
    </div>
    <el-card class="api-doc-card">
      <div class="doc-block doc-header" id="account-info">
        <div class="doc-title">账号信息</div>
        <div class="doc-row">
          <div class="doc-label">admin_id</div>
          <div class="doc-value">{{ adminId || "-" }}</div>
        </div>
        <div class="doc-row">
          <div class="doc-label">api_token</div>
          <div class="doc-value">
            <span>{{ apiToken || "未生成" }}</span>
            <el-button v-if="apiToken" type="text" size="mini" @click="copyToken">复制</el-button>
            <el-button v-else type="primary" size="mini" @click="generateToken">生成</el-button>
            <el-button v-if="apiToken" type="primary" size="mini" plain @click="handleUpdateToken">更新</el-button>
          </div>
        </div>
      </div>
      <div class="doc-block doc-toc" id="toc">
        <div class="doc-title">目录</div>
        <div class="doc-links">
          <button class="doc-link" type="button" @click="scrollTo('account-info')">1. 账号信息</button>
          <button class="doc-link" type="button" @click="scrollTo('request-spec')">2. 请求规范</button>
          <button class="doc-link" type="button" @click="scrollTo('sign-rule')">3. 签名规则</button>
          <button class="doc-link" type="button" @click="scrollTo('sign-example')">4. 签名示例</button>
          <button class="doc-link" type="button" @click="scrollTo('php-sign')">5. PHP 签名示例</button>
          <button class="doc-link" type="button" @click="scrollTo('order-create')">6. 创建订单</button>
          <button class="doc-link" type="button" @click="scrollTo('order-query')">7. 订单查询</button>
        </div>
      </div>
      <div class="doc-block" id="request-spec">
        <div class="doc-title">2. 请求规范</div>
        <div class="doc-text">所有接口统一使用 POST + JSON 方式请求。</div>
        <div class="doc-text">请求头：Content-Type=application/json。</div>
      </div>
      <div class="doc-block" id="sign-rule">
        <div class="doc-title">3. 签名生成规则</div>
        <div class="doc-text">1. 收集请求参数（JSON body），移除空值与签名字段。</div>
        <div class="doc-text">2. 按参数名 ASCII 升序排序，拼接成 key=value 的字符串，使用 & 连接。</div>
        <div class="doc-text">3. 末尾拼接 &key=密钥。</div>
        <div class="doc-text">4. 对拼接后的字符串做 MD5（32 位小写）。</div>
        <div class="doc-text">5. 将签名放入请求参数 signa。</div>
      </div>
      <div class="doc-block" id="sign-example">
        <div class="doc-title">4. 签名示例</div>
        <pre class="doc-code">amount=100&order_sn=SF20250101&key=api_token
signa=md5(...)</pre>
      </div>
      <div class="doc-block" id="php-sign">
        <div class="doc-title">5. PHP 签名生成示例</div>
        <pre class="doc-code">&lt;?php
/**
 * 生成签名
 * @param array $params 请求参数
 * @param string $apiToken 密钥
 * @return string
 */
function buildSign(array $params, string $apiToken): string
{
    unset($params['signa']);
    $filtered = [];
    foreach ($params as $key => $value) {
        if ($value === '' || $value === null) {
            continue;
        }
        $filtered[$key] = $value;
    }
    ksort($filtered, SORT_STRING);
    $pairs = [];
    foreach ($filtered as $key => $value) {
        $pairs[] = $key . '=' . $value;
    }
    $string = implode('&', $pairs) . '&key=' . $apiToken;
    return md5($string);
}</pre>
      </div>
      <div class="doc-block" id="order-create">
        <div class="doc-title">6. 创建订单</div>
        <div class="doc-text">路径：/api/order/create</div>
        <div class="doc-text">方法：POST（JSON）</div>
        <div class="doc-text">签名字段：signa</div>
        <div class="doc-text">签名参数顺序：aid、order_sn、money、user_ip、pay_type、notice_url、back_url。</div>
        <div class="doc-text">支付方式：wechat_pay（微信支付）、alipay（支付宝支付）。</div>
        <pre class="doc-code">{
  "aid": 1,                         //账号id
  "mid": 1,                         // 进件并审核通过的商户id
  "order_sn": "SF202501010001",     //商户单号 不允许重复
  "money": "100.00",                //下单金额
  "user_ip": "127.0.0.1",           //付款用户的ip
  "pay_type": "wechat_pay",         //支付方式
  "notice_url": "https://example.com/notify", // 回调通知ip
  "back_url": "https://example.com/return", // 支付后跳转页面
  "signa": "md5(...)"               //签名 参照上面的签名方式
}</pre>
      </div>
      <div class="doc-block" id="order-query">
        <div class="doc-title">7. 订单查询</div>
        <div class="doc-text">路径：/api/order/query</div>
        <div class="doc-text">方法：POST（JSON）</div>
        <div class="doc-text">签名字段：signa</div>
        <div class="doc-text">签名参数顺序：aid、order_sn。</div>
        <pre class="doc-code">{
  "aid": 1, //商户id
  "order_sn": "SF202501010001", //商户单号
  "signa": "md5(...)" //签名 参照上面的签名方式
}</pre>
        <div class="doc-text">返回：订单详情（成功）或错误信息。</div>
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      // 维护本地状态，避免直接修改props
      localGoods: [],
      adminId: "",
      apiToken: ""
    };
  },
  computed: {},
  // 页面挂载完成
  mounted() { },
  // 页面创建完成
  created() {
    // 初始化加载账号信息
    this.getAdminInfo();
  },
  methods: {
    // 滚动到指定区块
    scrollTo(id) {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    // 获取账号信息
    getAdminInfo() {
      this.$http_get("/admin/account/info", 1).then(res => {
        if (res && res.code === 200 && res.data) {
          this.adminId = res.data.id || res.data.admin_id || "";
          this.apiToken = res.data.api_token || res.data.api_key || "";
        }
      });
    },
    // 生成 API Token
    generateToken() {
      this.$http_post("/admin/account/update_api_token", {}, 1).then(res => {
        if (res && res.code === 200 && res.data) {
          this.apiToken = res.data.api_token || "";
        }
      });
    },
    // 更新 Token（确认是否生成新的）
    handleUpdateToken() {
      this.$confirm("确认生成新 token 吗？", "提示", {
        confirmButtonText: "生成",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        this.generateToken();
      }).catch(() => { });
    },
    // 复制 Token
    copyToken() {
      this.$copy(this.apiToken).then(() => {
        this.$message.success("已复制");
      }).catch(() => {
        this.$message.error("复制失败");
      });
    }
  }
};
</script>

<style>
.doc-header {
  margin-bottom: 12px;
}

.doc-toc {
  position: fixed;
  top: 90px;
  right: 24px;
  padding: 20px;
  border-bottom: 0;
  max-width: 220px;
  background: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
  z-index: 10;
}

.doc-links {
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* padding: 20px; */
}

.doc-link {
  background: none;
  border: 0;
  padding: 0;
  text-align: left;
  color: #409eff;
  cursor: pointer;
}

.doc-link:hover {
  text-decoration: underline;
}

.api-doc-card .el-card__body {
  position: relative;
  padding-right: 220px;
}

.doc-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.doc-label {
  width: 80px;
  color: #909399;
}

.doc-value {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #303133;
}

.doc-block {
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.doc-block:last-child {
  border-bottom: 0;
}

.doc-title {
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.doc-text {
  color: #606266;
  line-height: 1.8;
}

.doc-code {
  background: #f6f8fa;
  padding: 12px;
  border-radius: 6px;
  color: #2d2d2d;
  font-size: 12px;
  margin: 8px 0 0;
  white-space: pre-wrap;
}
</style>
